/**
 * Launch readiness — the pure brain behind the admin Platform Setup Wizard.
 *
 * Given the current state of each external-service integration (from the
 * `listIntegrationConfigs` callable), compute what's configured, what still
 * needs setup, and whether the platform is ready to launch. Pure + testable so
 * the wizard can never disagree with the model.
 */

/** How much a step matters for going live. */
export type LaunchRequirement = 'required' | 'recommended' | 'optional';

/** Per-step verdict. */
export type ReadinessState = 'done' | 'action_needed' | 'optional' | 'blocked';

/** Normalized input for one integration (mapped from the list-callable output). */
export interface ReadinessIntegrationInput {
  readonly service: string;
  readonly label: string;
  readonly launchRequirement: LaunchRequirement;
  /** 'live' = wired into deployed code; 'planned' = not yet built. */
  readonly status: 'live' | 'planned';
  readonly secrets: readonly {
    readonly name: string;
    readonly required: boolean;
    readonly isSet: boolean;
  }[];
  readonly runbook?: string | null;
}

export interface ReadinessStep {
  readonly service: string;
  readonly label: string;
  readonly requirement: LaunchRequirement;
  readonly state: ReadinessState;
  readonly detail: string;
  /** Names of required secrets still missing (empty when none). */
  readonly missingSecrets: readonly string[];
}

export interface LaunchReadiness {
  readonly steps: readonly ReadinessStep[];
  readonly requiredTotal: number;
  readonly requiredDone: number;
  /** True iff every `required` step is done. */
  readonly ready: boolean;
}

/**
 * A build-time client key — the class of setting that is NOT panel-configurable
 * and needs a rebuild + redeploy, not an edit in `/superadmin/integrations`.
 *
 * These were invisible to the wizard until 2026-08-20 (G3), which is the worst
 * possible place for a blind spot: the wizard's whole job is to say "you are
 * ready to launch", and it said so while `VITE_RECAPTCHA_SITE_KEY` was unset —
 * which makes EVERY App-Check-enforcing callable fail in production — and while
 * `VITE_FCM_VAPID_KEY` was unset, which kills push silently. A readiness check
 * that cannot see the things most likely to be forgotten is worse than none,
 * because it converts "I forgot" into "the tool told me I was fine".
 */
export interface BuildTimeKeyInput {
  /** The env var name, shown verbatim so the owner can search for it. */
  readonly name: string;
  readonly label: string;
  readonly requirement: LaunchRequirement;
  /** Whether this build was compiled with a non-empty value. */
  readonly isSet: boolean;
  /** What breaks in production when it is missing. */
  readonly consequence: string;
  /**
   * Which app builds consume it. The wizard runs inside the dashboard and can
   * only observe the DASHBOARD's build, so this is listed explicitly rather
   * than letting a green tick imply every app was checked.
   */
  readonly consumedBy: readonly string[];
}

/** Fold build-time keys into the same step shape the integrations produce. */
export function computeBuildTimeKeySteps(
  keys: readonly BuildTimeKeyInput[],
): readonly ReadinessStep[] {
  return keys.map((k) => {
    const observedHere = k.consumedBy.length === 1 && k.consumedBy[0] === 'dashboard';
    const scope = observedHere
      ? ''
      : ` Checked against THIS dashboard build only — ${k.consumedBy.join(', ')} are built separately, so confirm the same value is present in each.`;
    return {
      service: `build-key:${k.name}`,
      label: k.label,
      requirement: k.requirement,
      state: k.isSet ? 'done' : k.requirement === 'optional' ? 'optional' : 'action_needed',
      detail: k.isSet
        ? `Present in this build.${scope}`
        : `Missing. ${k.consequence} Set ${k.name} and REBUILD — this is a build-time value, not a panel setting.${scope}`,
      missingSecrets: k.isSet ? [] : [k.name],
    };
  });
}

/**
 * The legal entity that SUPPLIES the operator subscription and appears as
 * "FROM" on every invoice PDF — `platformBrands/{id}.legal` — plus the
 * platform's own SST status (`platformConfig/moneyPolicy.supplierTax`).
 */
export interface SupplierLegalInput {
  readonly companyName?: string;
  readonly companyRegistration?: string;
  readonly address?: string;
  readonly taxId?: string;
  readonly sstRegistered: boolean;
}

/**
 * Launch step for the invoice supplier block. Required: an operator invoice
 * issued without the supplier's registered name, registration number and
 * address is not a valid invoice, and these fields cannot be guessed — they
 * shipped as `TODO(owner)` blanks in the seed.
 */
export function computeSupplierLegalSteps(input: SupplierLegalInput): readonly ReadinessStep[] {
  const missing: string[] = [];
  if (!input.companyName?.trim()) missing.push('legal.companyName');
  if (!input.companyRegistration?.trim()) missing.push('legal.companyRegistration');
  if (!input.address?.trim()) missing.push('legal.address');
  const sstNote = input.sstRegistered
    ? ' SST: registered — every new operator invoice is a tax invoice with SST added.'
    : ' SST: not registered — no SST is charged on operator invoices (Money policy on the Plans page).';
  return [
    {
      service: 'supplier-legal',
      label: 'Invoice supplier details (legal entity)',
      requirement: 'required',
      state: missing.length === 0 ? 'done' : 'action_needed',
      detail:
        missing.length === 0
          ? `Set.${sstNote}`
          : `Fill ${missing.join(', ')} in Plans → Invoice supplier — they print as "FROM" on every operator invoice.${sstNote}`,
      missingSecrets: missing,
    },
  ];
}

/**
 * Fold the integrations into a readiness report.
 * - A step is `done` when all its required secrets are set (or it needs none).
 * - A `planned` (not-yet-built) integration is `blocked` if required, else `optional`.
 * - A missing `optional` integration is `optional`; a missing `required`/`recommended`
 *   one is `action_needed`.
 * - `ready` depends ONLY on `required` steps — recommended/optional never block launch.
 */
export function computeIntegrationReadiness(
  items: readonly ReadinessIntegrationInput[],
  buildTimeKeys: readonly BuildTimeKeyInput[] = [],
  /** Pre-computed steps that are not integrations, e.g. `computeSupplierLegalSteps`. */
  extraSteps: readonly ReadinessStep[] = [],
): LaunchReadiness {
  const integrationSteps: ReadinessStep[] = items.map((it) => {
    const requiredSecrets = it.secrets.filter((s) => s.required);
    const missingSecrets = requiredSecrets.filter((s) => !s.isSet).map((s) => s.name);

    let state: ReadinessState;
    let detail: string;

    if (it.status === 'planned') {
      state = it.launchRequirement === 'required' ? 'blocked' : 'optional';
      detail =
        it.launchRequirement === 'required'
          ? 'Not yet built — blocks launch.'
          : 'Not yet built — optional for now.';
    } else if (requiredSecrets.length === 0) {
      state = 'done';
      detail = 'No keys required.';
    } else if (missingSecrets.length === 0) {
      state = 'done';
      detail = 'All keys set.';
    } else {
      state = it.launchRequirement === 'optional' ? 'optional' : 'action_needed';
      detail = `Set ${missingSecrets.join(', ')}${it.runbook ? ` — see ${it.runbook}` : ''}.`;
    }

    return {
      service: it.service,
      label: it.label,
      requirement: it.launchRequirement,
      state,
      detail,
      missingSecrets,
    };
  });

  // Build-time keys sit in the SAME list and count toward `ready` exactly like
  // an integration. Keeping them in a separate advisory panel was the original
  // shape and is precisely how they got forgotten.
  const steps: ReadinessStep[] = [
    ...integrationSteps,
    ...computeBuildTimeKeySteps(buildTimeKeys),
    ...extraSteps,
  ];

  const requiredSteps = steps.filter((s) => s.requirement === 'required');
  const requiredDone = requiredSteps.filter((s) => s.state === 'done').length;
  const ready = requiredSteps.length > 0 && requiredDone === requiredSteps.length;

  return { steps, requiredTotal: requiredSteps.length, requiredDone, ready };
}
